import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import SmallSearchIcon from '../../../assets/icons/search-small.svg'
import {unique} from '../../../lib/helpers'
import styled from 'styled-components'
import {Avatar} from '../avatar/avatar'
import {
  DropdownBody,
  DropdownItem,
  DropdownItemIcon,
  DropdownPinnedAction,
  DropdownSection,
  DropdownSectionTitle
} from '../dropdown/content'
import {Dropdown, DropdownDirection} from '../dropdown/dropdown'
import {DropdownTitle} from '../dropdown/title'
import {Checkbox, PrimaryLabel, SecondaryLabel} from '../form/checkbox'
import {IconName} from '../icon/icon'
import {Option} from './helpers'
import {useSearchRegex} from '../../../hooks/use-search-regex'

const SearchBox = styled.div`
  border: 1px solid ${({theme}) => theme.grey10};
  border-radius: 4px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 16px 16px 12px;
`

const StyledSearchIcon = styled(SmallSearchIcon)`
  color: ${({theme}) => theme.black};
`

const SearchInput = styled.input`
  border: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  flex-grow: 1;
  font-size: ${({theme}) => theme.fontSizeXS};
`

interface OptionGroup<T extends string | number = string> {
  title?: ReactNode
  options: Option<T>[]
}

export type Options<T extends string | number> = Option<T> | OptionGroup<T>

const isOptionGroup = <T extends string | number>(
  o: Options<T>
): o is OptionGroup<T> => 'options' in o

interface BasedropdownSelectProps<T extends string | number = string> {
  label: string
  upLabel?: string
  options: (Option<T> | OptionGroup<T>)[]
  renderEmpty?: () => React.ReactNode
  renderFooter?: () => React.ReactNode
  getDisplayValue?: (value: T) => ReactNode | undefined
  tabIndex?: number
  order?: 'up' | 'down'
  iconLeft?: IconName
  iconRight?: IconName
  secondaryButtonRight?: React.ReactNode
  inline?: boolean
  matchContent?: boolean
  blue?: boolean
  hasAvatar?: boolean
  style?: React.CSSProperties
  dropdownDirection?: DropdownDirection
  groupSeparator?: boolean
  readOnly?: boolean
}

interface DropdownSelectProps<T extends string | number = string>
  extends BasedropdownSelectProps<T> {
  value?: T
  onChange: (value: T) => void
  isMultiSelect?: false
  minWidth?: number | false // use false to bypass default minWidth
  maxWidth?: number
  withSearch?: boolean
}

interface DropdownMultiSelectProps<T extends string | number = string>
  extends BasedropdownSelectProps<T> {
  value?: T[]
  onChange: (value: T[]) => void
  isMultiSelect: true
  minWidth?: number
  maxWidth?: number
  withSearch?: boolean
}

export const DropdownSelect = <T extends string | number = string>({
  label,
  upLabel,
  options,
  renderEmpty,
  renderFooter,
  tabIndex = 0,
  order,
  iconLeft,
  iconRight,
  secondaryButtonRight,
  inline,
  matchContent,
  blue,
  hasAvatar,
  minWidth = 360,
  maxWidth,
  style,
  dropdownDirection = 'down',
  groupSeparator,
  readOnly,
  withSearch = false,
  ...props
}: DropdownSelectProps<T> | DropdownMultiSelectProps<T>) => {
  const focusedOption = useRef<number>()
  const optionRefs = useRef<{[i: number]: HTMLAnchorElement}>({})
  const arrowListener = useRef<(e: KeyboardEvent) => void>()
  const [search, setSearch] = useState('')

  const mandatoryOptions =
    unique(
      options
        .flatMap(o => (isOptionGroup(o) ? o.options : o))
        .filter(o => o.mandatory)
        .map(o => o.value)
    ) ?? []

  const getDisplayValue = (v: T) => {
    let display: ReactNode | undefined = undefined
    if ('getDisplayValue' in props && props.getDisplayValue) {
      display = props.getDisplayValue(v)
    }
    if (!display) {
      const opt = options
        .flatMap(o => (isOptionGroup(o) ? o.options : o))
        .find(o => o.value === v)
      if (opt) {
        display = opt.text
      }
    }
    return display === undefined ? String(v) : display
  }

  const getValueString = (): undefined | ReactNode => {
    if (props.isMultiSelect && Array.isArray(props.value)) {
      return (
        unique([...mandatoryOptions, ...(props.value ?? [])])
          ?.map(getDisplayValue)
          // Note if a non-string ReactNode is supplied from
          // props.getDisplayValue this won't render correctly (can't 'join' nodes)
          .join(', ')
      )
    } else if (props.value && !Array.isArray(props.value)) {
      // TODO handle mandatory options better??
      // e.g. should mandatory be allowed for non-multi-select?
      return unique([...mandatoryOptions, props.value])?.map(getDisplayValue)
    } else {
      if (mandatoryOptions.length)
        return mandatoryOptions?.map(getDisplayValue).join(', ')
      return undefined
    }
  }

  const removeArrowListener = () => {
    if (arrowListener.current) {
      document.removeEventListener('keydown', arrowListener.current)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line functional/immutable-data
    arrowListener.current = (e: KeyboardEvent) => {
      if (Object.keys(optionRefs.current).length) {
        // If we preventDefault on all keys then we can't use the search box
        const mappedKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape']
        if (mappedKeys.includes(e.key)) {
          e.preventDefault()
        }

        const optionListLength = Object.keys(optionRefs.current).length
        if (focusedOption.current === undefined) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            focusedOption.current = 0
            optionRefs.current[0].focus()
          }
          return
        }

        if (e.key === 'ArrowDown') {
          if (focusedOption.current === optionListLength - 1) {
            focusedOption.current = -1
          }
          focusedOption.current += 1
          if (optionRefs.current[focusedOption.current]) {
            optionRefs.current[focusedOption.current].focus()
          }
        }

        if (e.key === 'ArrowUp') {
          if (focusedOption.current === 0) {
            focusedOption.current = optionListLength
          }
          focusedOption.current -= 1
          if (optionRefs.current[focusedOption.current]) {
            optionRefs.current[focusedOption.current].focus()
          }
        }

        if (e.key === 'Enter') {
          if (optionRefs.current[focusedOption.current]) {
            optionRefs.current[focusedOption.current].click()
          }
        }
      }
    }
    document.addEventListener('keydown', arrowListener.current)

    return removeArrowListener
  }, [withSearch])

  const searchRegex = useSearchRegex(search)

  const getSearchDisplayValue = useCallback(
    (title: string): ReactNode => {
      if (!searchRegex) {
        return title
      }
      // Split the name into parts, and wrap the parts that match the search text <strong>
      const parts = title.split(searchRegex)
      return parts.map((part, index) =>
        searchRegex.test(part) ? <strong key={index}>{part}</strong> : part
      )
    },
    [searchRegex]
  )

  // transform a potential mixture of Option and OptionGroup into a consistent
  // list of OptionGroup - each OptionGroup is appended as-is, and each Option
  // is appended to the default first group
  // apply search filtering here if present and styles to matched part of string.

  const optionGroups = options.reduce(
    (mut_acc, opt) => {
      if ('options' in opt) {
        const filteredOptions = searchRegex
          ? opt.options
              .filter(o => searchRegex.test(String(o.text)))
              .map(o => ({...o, text: getSearchDisplayValue(String(o.text))}))
          : opt.options
        // exclude empty groups
        if (filteredOptions.length) {
          mut_acc.push({...opt, options: filteredOptions})
        }
      } else {
        if (searchRegex && searchRegex.test(String(opt.text))) {
          mut_acc[0].options.push({
            ...opt,
            text: getSearchDisplayValue(String(opt.text))
          })
        } else {
          mut_acc[0].options.push(opt)
        }
      }
      return mut_acc
    },
    [{options: []}] as OptionGroup<T>[]
  )

  return (
    <Dropdown
      order={order}
      style={style}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onCloseDropdown={() => setSearch('')}
      forcedDropdownDirection={dropdownDirection}
      trigger={(handleClick, ref) => (
        <DropdownTitle
          style={style}
          blue={blue}
          matchContent={matchContent}
          inline={inline}
          iconLeft={iconLeft}
          secondaryButtonRight={secondaryButtonRight}
          iconRight={iconRight}
          tabIndex={tabIndex}
          ref={ref}
          handleClick={handleClick}
          label={label}
          upLabel={upLabel}
          value={getValueString()}
          readOnly={readOnly}
        />
      )}
      disabled={readOnly}
    >
      {closeDropdown => {
        optionRefs.current = {} // reset on each render, will be repopulated below

        return (
          <DropdownBody
            data-testid={'dropdown-body'}
            onClick={(event: React.MouseEvent) => {
              // Fixes auto close when clicking on the gap accidentally
              event.stopPropagation()
            }}
          >
            <>
              {withSearch && options.length ? (
                <DropdownFilter onChange={setSearch} value={search} />
              ) : null}
              {optionGroups.length > 0
                ? optionGroups.map((group, i) => {
                    const commonProps = {
                      ...group,
                      key: `${props.value}-${i}`,
                      closeDropdown,
                      hasAvatar: hasAvatar,
                      optionRefs: optionRefs
                    }

                    // ternary required here otherwise TS doesn't discriminate
                    // the type of props.value and props.onChange
                    return props.isMultiSelect ? (
                      <OptionList<T>
                        {...commonProps}
                        isMultiSelect={true}
                        value={props.value}
                        onChange={props.onChange}
                        topSeparator={groupSeparator && i > 0}
                      />
                    ) : (
                      <OptionList<T>
                        {...commonProps}
                        isMultiSelect={false}
                        value={props.value}
                        onChange={props.onChange}
                        topSeparator={groupSeparator && i > 0}
                      />
                    )
                  })
                : renderEmpty
                ? renderEmpty()
                : null}
            </>
            {options.length > 0 && renderFooter && (
              <DropdownPinnedAction>{renderFooter()}</DropdownPinnedAction>
            )}
          </DropdownBody>
        )
      }}
    </Dropdown>
  )
}

interface BaseListProps<T extends string | number = string> {
  options: Option<T>[]
  closeDropdown: () => void
  hasAvatar?: boolean
  optionRefs: React.MutableRefObject<{[i: number]: HTMLAnchorElement}>
  title?: ReactNode
  topSeparator?: boolean
}

interface ListProps<T extends string | number = string>
  extends BaseListProps<T> {
  value?: T
  onChange: (value: T) => void
  isMultiSelect: false
}

interface MultiListProps<T extends string | number = string>
  extends BaseListProps<T> {
  value?: T[]
  onChange: (value: T[]) => void
  isMultiSelect: true
}

const OptionList = <T extends string | number = string>(
  props: ListProps<T> | MultiListProps<T>
) => {
  const renderList = (list: Option<T>[]) => {
    return (
      <>
        {list.map((option, i) => {
          const [optionValue, optionText]: [T, string | ReactNode] =
            typeof option === 'object'
              ? [option.value, option.text]
              : [option, String(option)]
          return (
            <DropdownItem checkboxRow={true} key={i}>
              {props.isMultiSelect ? (
                <Checkbox
                  onChange={(checked, event) => {
                    const values = props.value ?? []
                    // always include the mandatory options
                    const mandatory = props.options
                      .filter(o => o.mandatory)
                      .map(o => o.value)
                    const newValues = checked
                      ? unique([...values, ...mandatory, optionValue])
                      : unique([...values, ...mandatory])?.filter(
                          v => v !== optionValue
                        )
                    props.onChange(newValues ?? [])
                    event.stopPropagation()
                  }}
                  checked={
                    !!props.value?.find(v => v === optionValue) ||
                    !!option.mandatory
                  }
                  disabled={option.mandatory || option.disabled}
                  label={
                    option.mandatory ? (
                      <span style={{opacity: '70%'}}>{String(optionText)}</span>
                    ) : (
                      String(optionText)
                    )
                  }
                  secondary={
                    typeof option === 'object' && option.secondary
                      ? option.secondary
                      : undefined
                  }
                  thumbnail={
                    props.hasAvatar && (
                      <Avatar
                        name={
                          typeof option === 'object' && option.secondary
                            ? option.secondary
                            : String(optionText)
                        }
                      />
                    )
                  }
                />
              ) : (
                <a
                  style={option.disabled ? {cursor: 'not-allowed'} : {}}
                  onClick={event => {
                    if (option.disabled) {
                      return
                    }
                    props.onChange(optionValue)
                    props.closeDropdown()
                    event.stopPropagation()
                  }}
                  ref={ref => {
                    if (ref) {
                      props.optionRefs.current[i] = ref
                    }
                  }}
                  tabIndex={i + 1}
                >
                  {String(props.value) === optionValue ? (
                    <DropdownItemIcon variant="color" icon="check" />
                  ) : (
                    <DropdownItemIcon />
                  )}

                  <PrimaryLabel>{optionText}</PrimaryLabel>
                  {typeof option === 'object' && option.secondary && (
                    <SecondaryLabel>{option.secondary}</SecondaryLabel>
                  )}
                </a>
              )}
            </DropdownItem>
          )
        })}
      </>
    )
  }
  return props.title || props.topSeparator ? (
    <DropdownSection>
      {props.title && (
        <DropdownSectionTitle>{props.title}</DropdownSectionTitle>
      )}
      {renderList(props.options)}
    </DropdownSection>
  ) : (
    renderList(props.options)
  )
}

const DropdownFilter = ({
  onChange,
  value
}: {
  onChange: (text: string) => void
  value: string
}) => {
  return (
    <SearchBox
      onSubmit={e => {
        if (!e.defaultPrevented) {
          e.preventDefault()
        }
      }}
    >
      <StyledSearchIcon />
      <SearchInput
        id="search-options"
        onChange={e => onChange(e.target.value)}
        placeholder="Search..."
        type="search"
        value={value}
      />
    </SearchBox>
  )
}
